import classNames from "classnames";
import type { MouseEvent, VFC } from "react";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import { KibelaSymbol } from "../shared/kibela_symbol";
import { OnlySmartphone, useIsUnderSmartphone } from "../shared/ResponsiveContainer";

import { NotificationButtonWithBadge } from "./NotificationButtonWithBadge";
import { NotificationModel } from "./NotificationModel";
import { NotificationTabs } from "./NotificationTabs";

import { useListServiceNotificationsQuery, useMarkServiceNotificationAsReadMutation } from "~/client/generated/schema";
import { useClickOutside } from "~/client/hooks/useClickOutside";
import { logException } from "~/client/utils/log_exception";

interface Props {
  isEnabledServiceNotificationWeb: boolean;
  intl: any;
}

// NOTE: serverではviewportは存在しないためisUnderSmartphoneは必ずfalseになる。
// しかしclientでtrueになった場合、返却されるDOMが異なるとReactのhydrateでエラーになる
// そのためにuseEffectによってclientでレンダリングするDOMを差し替える
const NotificationButtonIcon: VFC = () => {
  const [iconId, setIconId] = useState<"bell" | "bell_fill">("bell");
  const isUnderSmartphone = useIsUnderSmartphone();

  useEffect(() => {
    setIconId(isUnderSmartphone ? "bell_fill" : "bell");
  }, [isUnderSmartphone]);

  return <KibelaSymbol id={iconId} />;
};

export const NotificationCenter: VFC<Props> = (props) => {
  const { isEnabledServiceNotificationWeb, intl } = props;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notificationModel, setNotificationModel] = useState(new NotificationModel());
  const { data } = useListServiceNotificationsQuery({ variables: { first: 25 } });
  const [unreadServiceNotification, setUnreadServiceNotification] = useState(false);
  const [markServiceNotificationAsReadMutation, { called }] = useMarkServiceNotificationAsReadMutation();

  useEffect(() => {
    const fetchNotification = async () => {
      const model = await notificationModel.fetchUnreadCount();
      setNotificationModel(model);
    };

    fetchNotification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = async (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();

    const open = !isDropdownOpen;
    setIsDropdownOpen(open);

    if (open && !notificationModel.isNotificationsLoaded) {
      const model = await notificationModel.fetchAndMarkAsReadNotifications();
      setNotificationModel(model);
    }
  };

  useEffect(() => {
    if (data === undefined) {
      return;
    }

    if (!isEnabledServiceNotificationWeb) {
      setUnreadServiceNotification(false);
      return;
    }

    const unread = data.serviceNotifications.nodes.some((notification) => !notification.read);
    setUnreadServiceNotification(unread);
  }, [data, isEnabledServiceNotificationWeb]);

  const handleReadServiceNotifications = () => {
    if (called || data === undefined) {
      return;
    }

    markServiceNotificationAsReadMutation({
      variables: { serviceNotificationIds: data.serviceNotifications.nodes.map((n) => n.id) },
    })
      .then(() => setUnreadServiceNotification(false))
      .catch((e) => logException(e));
  };

  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setIsDropdownOpen(false));

  return (
    <div ref={ref} className="notificationCenter dropdownBox-container">
      <NotificationButtonWithBadge
        onClick={handleClick}
        unreadNotificationCount={notificationModel.unreadCount}
        unreadServiceNotification={unreadServiceNotification}
        id="notificationCenterButton"
        className="notificationCenter__button"
        badgeClassName="notificationCenter__button__badge"
      >
        <span
          className={classNames(
            "notificationCenter__button__icon",
            isDropdownOpen && "notificationCenter__button__icon--active",
          )}
        >
          <NotificationButtonIcon />
        </span>
      </NotificationButtonWithBadge>
      <OnlySmartphone
        className={classNames(
          "notificationCenter__button__label",
          isDropdownOpen && "notificationCenter__button__label--active",
        )}
      >
        <label htmlFor="notificationCenterButton">
          <FormattedMessage id="smartphoneNotificationCenter.notification" />
        </label>
      </OnlySmartphone>

      {isDropdownOpen && (
        <div className="notificationCenter-dropdown dropdownBox">
          <NotificationTabs
            isNotificationsLoaded={notificationModel.isNotificationsLoaded}
            isNotificationsLoadError={notificationModel.isError}
            notifications={notificationModel.notifications}
            serviceNotifications={data?.serviceNotifications.nodes}
            unreadServiceNotification={unreadServiceNotification}
            onReadServiceNotifications={handleReadServiceNotifications}
            intl={intl}
          />
        </div>
      )}
    </div>
  );
};
